<template>
    <div id="roles" class="setting-page-content-wrapper">
        <template v-if="isEdit">
            <role-edit 
                :roleId="roleId" 
                @saved="onRoleSaved" 
                @cancelled="goBackToRoles" 
                ref="roleEditComponent"
            />
        </template>
        <template v-else>
            <supsis-table
                :key="paginationCursor.page"
                :columns="cols"
                :rows="roles"
                :bottom-pagination="true"
                :pagination-cursor="paginationCursor"
                @cursor-changed="(cursor) => handlePageChange(cursor)"
            >
                <template #header>
                    <div class="d-flex justify-content-between align-items-center">
                        <a-input-search
                            v-model="searchText"
                            :placeholder="$t('message.search')"
                            class="search-input"
                            @search="onSearch"
                        />
                        <SupsisButton ghost @click="createNewRole">
                            <md-icon>add</md-icon>
                            {{ $t("message.createRole") }}
                        </SupsisButton>
                    </div>
                </template>
            </supsis-table>
        </template>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { log2 } from "../../../../../../utils/log2";
import * as _ from "lodash";
import { PERMISSION_TYPES } from "../../../../../../language/permissionTypes";
import { sendSocketMessage } from "../../../../../../utils/socketStream";
import Pagination from "../../../../../../components/Pagination.vue";
import ActionEdit from "../../../../../../components/svg/ActionEdit.vue";
import ActionDelete from "../../../../../../components/svg/ActionDelete.vue";
import SupsisTable from "../../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../components/SupsisButton.vue";
import CreateButton from "../../../../../../components/CreateButton.vue";
import RoleEdit from "./RoleEdit.vue";

library.add(faSpinner);
const log = log2("Roles");

export default {
    name: "Roles",
    components: {
        CreateButton,
        SupsisButton,
        SupsisTable,
        Pagination,
        ActionEdit,
        ActionDelete,
        RoleEdit
    },
    props: {
        isEdit: {
            type: Boolean,
            default: false
        },
        roleId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            PERMISSION_TYPES,
            isBusy: true,
            loading: false,
            searchText: undefined,
            roles: [],
            editingRole: null,
            cols: [
                {
                    field: "name",
                    key: "name",
                    title: this.$t("message.title"),
                },
                {
                    align: "right",
                    field: "actionsSlot",
                    key: "actionsSlot",
                    title: this.$t("message.transactions"),
                    fixed: "right",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const renderEditButton = () => {
                            return (
                                this.checkPermission(PERMISSION_TYPES["role.update"].tr.name) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center"
                                        on-click={() => this.editRole(row)}
                                    >
                                        <span class="material-icons" style="color:#1bc5bd;">edit</span>
                                    </a-button>
                                )
                            );
                        };
                        const renderDeleteButton = () => {
                            return (
                                this.checkPermission(PERMISSION_TYPES["role.delete"].tr.name) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center"
                                        on-click={() => this.remove(row._id)}
                                    >
                                        <ActionDelete />
                                    </a-button>
                                )
                            );
                        };
                        return (
                            <div class="d-flex w-100 justify-content-end">
                                {renderEditButton()}
                                {renderDeleteButton()}
                            </div>
                        );
                    },
                },
            ],
            currentSort: {
                columnKey: "name",
                order: "ascend",
            },
            paginationCursor: {
                total: 0,
                page: 1,
                perPage: 10,
            },
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            permissions: (state) => state.permission.permissions,
            isAdmin: (state) => state.auth.adminUser != null,
        }),
        ...mapGetters({
            groupedPermissions: "permission/groupedPermissions",
            isDarkMode: "user/isDarkMode",
            checkPermission: "auth/checkPermission",
        }),
        filteredRoles() {
            return this.searchText
                ? this.roles.filter((x) =>
                      x.name.toLocaleLowerCase("tr-TR").includes(this.searchText.toLocaleLowerCase("tr-TR"))
                  )
                : this.roles;
        },
        total() {
            return this.filteredRoles.length;
        },
        currentPageData() {
            const start = (this.paginationCursor.page - 1) * this.paginationCursor.perPage;
            const end = start + this.paginationCursor.perPage;
            return this.filteredRoles.slice(start, end);
        },
    },
    methods: {
        ...mapActions({
            getPermissions: "permission/getPermissions",
            getRoles: "role/getRoles",
            getRole: "role/getRole",
            createRole: "role/createRole",
            updateRole: "role/updateRole",
            deleteRole: "role/deleteRole",
        }),
        createNewRole() {
            this.$emit('route-change', true, 'new');
        },
        editRole(role) {
            this.editingRole = role;
            this.$emit('route-change', true, role._id);
        },
        goBackToRoles() {
            this.$emit('route-change', false, null);
        },
        onRoleSaved() {
            this.goBackToRoles();
            this.get(); 
        },
        handlePageChange(cursor) {
            this.paginationCursor.page = cursor.page;
            this.paginationCursor.perPage = cursor.perPage;
            this.get();
        },
        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
        },
        get(getPermissions = false) {
            this.isBusy = true;
            this.getRoles({ perPage: this.paginationCursor.perPage, page: this.paginationCursor.page })
                .then((response) => {
                    this.roles = response.data;
                    this.paginationCursor.total = response.total;
                    this.isBusy = false;

                    if (getPermissions) {
                        this.getPermissions({ perPage: 100, page: 1 });
                    }
                })
                .catch((err) => {
                    log.error(err);
                    this.isBusy = false;
                });
        },
        async loadRoleForEdit() {
            if (this.isEdit && this.roleId !== 'new') {
                try {
                    this.editingRole = await this.getRole(this.roleId);
                } catch (error) {
                    log.error(error);
                    this.$toasted.error("Rol yüklenirken bir hata oluştu", {
                        duration: 3000,
                        position: "bottom-center"
                    });
                    this.goBackToRoles();
                }
            } else if (this.isEdit && this.roleId === 'new') {
                this.editingRole = { _id: 'new', name: '', permissions: [] };
            }
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("message.deletePermissionQuestion"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.deleteRole(id)
                        .then((x) => {
                            this.roles = this.roles.filter((r) => r._id !== id);
                            this.$toasted.show(this.$t("message.roleDeleted"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        })
                        .catch((err) => {
                            log.error(err.response, err.message);
                            const errMsg =
                                err.response && err.response.data && err.response.data.localizedMessages
                                    ? err.response.data.localizedMessages[this.$i18n.locale]
                                    : err.message;
                            this.$toasted.error(errMsg, {
                                duration: 4000,
                                position: "bottom-center",
                            });
                        });
                });
        },
    },
    async mounted() {
        await this.get(true);
        
        if (this.isEdit) {
            await this.loadRoleForEdit();
        }
    },
    async beforeRouteUpdate(to, from, next) {
        // URL değiştiğinde gerekli veriyi yükle
        next();
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
    watch: {
        isEdit: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.loadRoleForEdit();
                }
            }
        },
        roleId: {
            handler(newVal) {
                if (this.isEdit && newVal) {
                    this.loadRoleForEdit();
                }
            }
        },
        paginationCursor: {
            deep: true,
            handler() {
                if (!this.isEdit) {
                    this.get();
                }
            },
        }
    },
};
</script>

<style lang="scss">
#roles {
    position: relative;
    height: 100%;
    
    .page-header {
        display: flex;
        flex-direction: column;
        gap: 10px;
        border-bottom: 1px solid var(--color-border-default);
        padding-bottom: 16px;
        margin-bottom: 16px;

        .title {
            font-size: 18px;
            font-weight: 700;
            color: var(--color-text-strong-default);
        }

        .subtitle {
            font-size: 14px;
            font-weight: 400;
            color: var(--color-text-default);
        }
    }

    .groupTitle {
        border-radius: 6px;
        height: 35px;
        display: flex;
        align-items: center;
        margin-bottom: 5px;
        padding: 4px 6px;
        cursor: pointer;
        justify-content: space-between;
        background-color: rgb(243, 246, 249);

        .md-ripple {
            justify-content: flex-end !important;
        }

        label {
            font-weight: 600;
            margin: 0;
            cursor: pointer;
        }
    }

    .md-button {
        // margin: 4px;
        margin: 0;
        padding: 0 14px;
    }

    .md-disabled {
        pointer-events: none;
    }

    .md-button.md-primary.md-ghost {
        &:hover {
            color: white;
            background-color: var(--md-theme-default-primary);
        }
    }
}
</style>
